import React, { useEffect, useRef, useState } from 'react'
import MapComp, { Coordinate } from './MapComp'
import { InputGroupPropsTypes, TypeTypes } from '../inputComp/InputGroupType'
import { CalenderIcon, DescriptionIcon } from '../iconSet/IconSet'
import InputGroup from '../inputComp/InputGroup'
import { APIEwSubmitTemplate, APIRequestSubmitEwTemplate, APITemplateResponse } from '../../services/APITemplateService'
import { convertToOffsetDateTime, formatDate } from './CommonMethods'
import AlertDialog from '../popupComp/AlertDialog'
import LoadingScreen from '../loadingScreenComp/LoadingScreen'
import { useLocation, useNavigate } from 'react-router-dom'

const TriggerTemplate = () => {

    const navigate = useNavigate()
    const COUNTRY = process.env.REACT_APP_COUNTRY
    
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const data = JSON.parse(params.get("data") || "{}") as APITemplateResponse;
    
    const [alertDialogState, setAlertDialogState] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertDescription, setAlertDescription] = useState('');
    const [alertYesCallback, setAlertYesCallback] = useState<(() => void) | undefined>(undefined); // for Yes
    const [alertOkCallback, setAlertOkCallback] = useState<(() => void) | undefined>(undefined);  // for OK

    const showAlertDialog = (title: string, description: string, yesCallback?: () => void, okCallback?: () => void ) => {
        setAlertTitle(title);
        setAlertDescription(description);
        setAlertYesCallback(() => yesCallback); // Will show Yes/Cancel if defined
        setAlertOkCallback(() => okCallback);   // Will trigger only on OK
        setAlertDialogState(true);
    }

    // Loading screen
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const [selectedCoordinate, setSelectedCoordinate] = useState<Coordinate[]>([])
    
    const validityStartRef = useRef<HTMLInputElement | null>(null)
    const validityEndRef = useRef<HTMLInputElement | null>(null)

    const messageEnRef = useRef<HTMLTextAreaElement | null>(null)

    // Polygon
    const polygonRef = useRef<HTMLTextAreaElement>(null)

    const formData: InputGroupPropsTypes[] = [
        {
            type: 'date-time',
            icon: <CalenderIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter start valid period',
            label: 'Validity Start Time',
            name: 'validityStartTime',
            required: true,
            componentInputElement: validityStartRef,
        },
        {
            type: 'date-time',
            icon: <CalenderIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            label: 'Validity End Time',
            name: 'validityEndTime',
            required: true,
            componentInputElement: validityEndRef,
        }
    ]

    const formData2: InputGroupPropsTypes[] = [
        {
            type: 'text-area',
            icon: <DescriptionIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'English Message',
            label: 'English message',
            name: 'english_message',
            required: false,
            componentTextArea: messageEnRef,
        }
    ]

    // String generator from Object
    const getCoordinateString = (coordinate: Coordinate[]): string => {
        return coordinate.map((item) => `${item.lat.toFixed(5)} ${item.lon.toFixed(5)}`).join(", ")
    }

    // Handle click back
    const handleClickBack = () => {
        navigate("/early_warning/generate_warning")
    }

    // Handle submit
    const handleClickSubmit = async () => {
        setIsLoading(true)
        const coordinate = selectedCoordinate

        if (coordinate.length === 0) 
        {
            setIsLoading(false)
            showAlertDialog("Error", `❌ Please select the affected areas on the map`)
            return
        }
        setIsLoading(false)
        showAlertDialog("Confirm Submit", "Are you sure you want to submit this early warning?", () => submitTemplate())
    }

    const submitTemplate = async () => {
        setIsLoading(true)
        if (!selectedCoordinate || !validityStartRef.current || !validityEndRef.current || !messageEnRef.current) return

        const requestBody: APIRequestSubmitEwTemplate = {
            type: 'temp',
            templateId: data.id,
            polygon: getCoordinateString(selectedCoordinate),
            startDate: convertToOffsetDateTime(validityStartRef.current.value),
            endDate: convertToOffsetDateTime(validityEndRef.current.value),
            publishDate: convertToOffsetDateTime(new Date().toISOString()),
            messageEn: messageEnRef.current.value,
        }

        const response = await APIEwSubmitTemplate(requestBody)

        if (response.status)
        {
            showAlertDialog("Success", "✅ Early warning submitted successfully!", undefined, () => navigate("/early_warning/generate_warning"));
        }
        else 
        {
            showAlertDialog("Error", `❌ ${response.message}`)
        }
        setIsLoading(false)
    }

    useEffect(() => {
        if (!data) return
        if (!messageEnRef.current) return

        messageEnRef.current.value = data.messageEn;
    }, [])

    useEffect(() => {
        if (!polygonRef.current) return

        if (selectedCoordinate.length > 0 || selectedCoordinate !== undefined || selectedCoordinate !== null)
        {
            polygonRef.current.value = getCoordinateString(selectedCoordinate)
        }
        else 
        {
            polygonRef.current.value = ""
        }
    }, [selectedCoordinate])

    useEffect(() => {
        if (validityStartRef.current && validityEndRef.current)
        {
            const now = new Date()
            const twoDaysLater = new Date()
            twoDaysLater.setDate(now.getDate() + 2)

            validityStartRef.current.value = formatDate(now)
            validityEndRef.current.value = formatDate(twoDaysLater)
        }
    }, [])

    return (
        <div className='template-trigger-wrapper'>
            <div className='map-wrapper'>
                <MapComp polygon={setSelectedCoordinate} clearButton={false}/>
            </div>
            <div className='selected-polygon-wrapper'>
                <label htmlFor="polygon-string">Selected area polygon</label>
                <textarea placeholder='No any area selected' disabled={true} ref={polygonRef}/>
            </div>
            <div className='form-wrapper'>
                <label htmlFor="polygon-string">Validity period</label>
                {formData.map((field, index) => (
                    <InputGroup key={index} {...field}/>
                ))}
            </div>
            <div className='form-wrapper'>
                <label htmlFor="polygon-string">Descriptions</label>
                {formData2.map((field, index) => (
                    <InputGroup key={index} {...field}/>
                ))}
            </div>
            <div className='button-wrapper'>
                <button onClick={(e) => handleClickBack()}>Back</button>
                <button onClick={(e) => handleClickSubmit()}>Submit</button>
            </div>
            {alertDialogState && (
                <AlertDialog title={alertTitle} description={alertDescription} windowState={alertDialogState} setWindowState={setAlertDialogState} function={alertYesCallback} okFunction={alertOkCallback} />
            )}
            
            {isLoading && (
                <LoadingScreen />
            )}
        </div>
    )
}

export default TriggerTemplate